/**
 * The edit planner (docs/pipeline-v2/edits.html): one edit after ready →
 * what runs by itself, what is marked stale («обновить»), what appears new
 * («нарисовать / озвучить»), what goes — as CONCRETE artifact keys of this
 * story (blocks, pages, narrations, (entity, state) sheets, exports).
 *
 * Driven by EDITS of model/editModel.ts: the rule says WHICH artifact kinds
 * are auto / stale / new / gone; the scope of the edit (the block, the entity
 * and its states, the page, the narration) and the illustration template say
 * WHICH instances. Pure — the guards (lock, open print order) arrive in
 * `state.locks`, revert-by-hash keys in `opts.reusableKeys`.
 *
 * Owner decisions (plan §15.1, §16, §16.2): no edit while any layer of the
 * story or its translation generates; «обновить» is an add-on order with its
 * own estimate; an edit that stales a lot of paid work shows the estimate and
 * needs a confirmation; auto LLM steps are free with a rate limit; a revert
 * brings back the previous pages / audio for free; translations are copies
 * marked «исходник изменился»; published = snapshot → a new version for
 * moderation; format / style / template = a new illustration; block 0 is
 * pinned; a block voice stays within the narration's model.
 */
import { EDITS, NEVER, type EditRule } from '../model/editModel';
import type {
  ArtifactRef, ConfirmReason, EditEstimate, EditIllustration, EditKind, EditNotice,
  EditPage, EditPlan, EditTarget, ForbiddenReason, PlanEditOptions, StateRef, StoryEditState,
} from './types';

/** = MAX_COMIC_PAGES_PER_BLOCK (illustrations/services/blockPages.ts): a new comic block is estimated at its upper bound */
export const MAX_NEW_PAGES_PER_COMIC_BLOCK = 2;
/** = FREE_REDRAWS_PER_STORY (illustrations/services/redrawQuota.ts, imports the DB so not imported here) */
export const FREE_REDRAWS = 5;
/** LLM rewrites of one block (ED-02) */
export const MAX_BLOCK_REGENS = 3;
export const DEFAULT_BIG_EDIT_UNITS = 8;
export const DEFAULT_AUTO_RUN_LIMIT = 20;

export const EDIT_KINDS: Record<EditKind, { ed: string; rule: string }> = {
  block_text: { ed: 'ED-01', rule: 'Текст блока' },
  block_regen: { ed: 'ED-02', rule: 'Перегенерировать блок (LLM)' },
  title: { ed: 'ED-03', rule: 'Название' },
  stress: { ed: 'ED-04', rule: 'Ударения в блоке' },
  rename: { ed: 'ED-05', rule: 'Переименовать героя' },
  entity_look: { ed: 'ED-06', rule: 'Описание / наряд сущности' },
  hero_version: { ed: 'ED-07', rule: 'Герой в библиотеке (новая версия)' },
  bind_hero: { ed: 'ED-08', rule: 'Привязать героя библиотеки' },
  add_block: { ed: 'ED-09', rule: 'Добавить блок' },
  delete_block: { ed: 'ED-10', rule: 'Удалить блок' },
  reorder: { ed: 'ED-11', rule: 'Переставить блоки' },
  redraw_page: { ed: 'ED-12', rule: 'Перерисовать страницу (квота 5)' },
  balloon: { ed: 'ED-13', rule: 'Реплика в кадре (комикс)' },
  cover_cast: { ed: 'ED-14', rule: 'Кто на обложке' },
  recording: { ed: 'ED-15', rule: 'Запись родителя в блоке' },
  block_voice: { ed: 'ED-16', rule: 'Голос блока' },
  music: { ed: 'ED-17', rule: 'Музыка' },
  dedication: { ed: 'ED-18', rule: 'Посвящение' },
  format_style: { ed: 'ED-19', rule: 'Формат / стиль книги' },
  delete_hero: { ed: 'ED-20', rule: 'Удалить героя / отозвать согласие' },
  delete_story: { ed: 'ED-21', rule: 'Удалить историю' },
};

/** Edits that rewrite many blocks: always shown with the estimate. */
const BIG_KINDS = new Set<EditKind>(['rename', 'bind_hero']);
/** Auto steps that are an LLM call (rate-limited); the rest is code. */
const LLM_STEPS = new Set(['text', 'scene', 'scene_x', 'check', 'newent', 'prep', 'dir', 'sb', 'sbc', 'title']);
const EXPORT_ARTIFACTS = new Set(['pdf', 'print', 'video', 'pub', 'yt']);
const ILL_ARTIFACTS = new Set(['page', 'cover', 'sheet', 'refsp', 'refsc', 'balloon', 'sb', 'sbc']);

type Klass = 'auto' | 'stale' | 'new' | 'gone';

/** Which instances of each artifact kind the edit touches. */
interface Scope {
  blocks: string[];
  narrations: string[];
  /** illustrations whose block storyboard / pages are in play */
  blockIlls: string[];
  /** extra page filter (entity look: pages with the entity in frame) — OR-ed with blocks × blockIlls */
  pageFilter?: (ill: EditIllustration, page: EditPage) => boolean;
  /** a single page (redraw, balloon) */
  page?: { illustrationId: string; pageId: string };
  /** a new block: pages do not exist yet → `page:i:b:*` */
  placeholderBlock?: string;
  covers: string[];
  sheets: (ill: EditIllustration) => StateRef[];
  title: boolean;
  entities: string[];
  translations: boolean;
}

const emptyScope = (): Scope => ({
  blocks: [], narrations: [], blockIlls: [], covers: [], sheets: () => [], title: false, entities: [], translations: false,
});

const has = (refs: StateRef[], e: string, s?: string) => refs.some((r) => r.entityId === e && (s === undefined || r.stateId === s));
const coverOf = (ill: EditIllustration) => ill.pages.find((p) => p.kind === 'cover');
const blocksWith = (state: StoryEditState, e: string) => state.blocks.filter((b) => has(b.entities, e)).map((b) => b.blockId);
const missingSheets = (ill: EditIllustration, wanted: StateRef[] | undefined) =>
  (wanted ?? []).filter((w) => !has(ill.sheets, w.entityId, w.stateId))
    .filter((w, i, a) => a.findIndex((x) => x.entityId === w.entityId && x.stateId === w.stateId) === i);

function forbidden(kind: EditKind, reason: ForbiddenReason): EditPlan {
  const { ed, rule } = EDIT_KINDS[kind] ?? { ed: '', rule: '' };
  return { kind, ed, rule, autoSteps: [], stale: [], new: [], gone: [], reused: [], confirmRequired: [], notices: [], forbiddenReason: reason };
}

/** Scope of the edit; null = the target does not exist in the story. */
function scopeOf(kind: EditKind, t: EditTarget, state: StoryEditState): Scope | null {
  const s = emptyScope();
  const allNarr = state.narrations.map((n) => n.narrationId);
  const allIll = state.illustrations.map((i) => i.illustrationId);
  const block = t.blockId ? state.blocks.find((b) => b.blockId === t.blockId) : undefined;
  const ent = t.entityId ? state.entities.find((e) => e.entityId === t.entityId) : undefined;
  const ill = t.illustrationId ? state.illustrations.find((i) => i.illustrationId === t.illustrationId) : undefined;
  const narr = t.narrationId ? state.narrations.find((n) => n.narrationId === t.narrationId) : undefined;
  const textIlls = (sceneChanged: boolean) => state.illustrations.filter((i) => i.drawsText || sceneChanged).map((i) => i.illustrationId);

  switch (kind) {
    case 'block_text':
    case 'block_regen': {
      if (!block) return null;
      const sceneChanged = !!t.sceneChanged || !!t.newStates?.length;
      s.blocks = [block.blockId];
      s.narrations = allNarr;
      // page stale on text only where the template draws text (or the scene changed)
      s.blockIlls = textIlls(sceneChanged);
      s.sheets = (i) => missingSheets(i, t.newStates);
      s.translations = true;
      return s;
    }
    case 'title': {
      const b0 = state.blocks.find((b) => b.index === 0);
      s.blocks = b0 ? [b0.blockId] : [];
      s.narrations = allNarr;
      s.covers = allIll;
      s.title = true;
      return s;
    }
    case 'stress': {
      if (!block) return null;
      s.blocks = [block.blockId];
      s.narrations = allNarr;
      return s;
    }
    case 'rename': {
      if (!ent) return null;
      s.entities = [ent.entityId];
      s.blocks = t.blockIds ?? blocksWith(state, ent.entityId);
      s.narrations = allNarr;
      s.blockIlls = textIlls(false); // pages only where the name is drawn
      s.title = !!t.nameInTitle;
      s.covers = t.nameInTitle ? allIll : [];
      s.translations = true;
      return s;
    }
    case 'entity_look': {
      if (!ent) return null;
      const states = t.stateIds ?? ent.states;
      s.entities = [ent.entityId];
      s.blocks = blocksWith(state, ent.entityId); // check: is the old look in the text?
      s.pageFilter = (_i, p) => p.kind === 'page' && p.inFrame.some((f) => f.entityId === ent.entityId && states.includes(f.stateId));
      s.covers = state.illustrations.filter((i) => coverOf(i)?.inFrame.some((f) => f.entityId === ent.entityId && states.includes(f.stateId))).map((i) => i.illustrationId);
      s.sheets = (i) => i.sheets.filter((r) => r.entityId === ent.entityId && states.includes(r.stateId));
      return s;
    }
    case 'hero_version': {
      if (!ent) return null;
      s.entities = [ent.entityId];
      return s;
    }
    case 'bind_hero': {
      if (!ent) return null;
      s.entities = [ent.entityId];
      s.blocks = t.blockIds ?? blocksWith(state, ent.entityId);
      s.narrations = allNarr;
      s.blockIlls = textIlls(false);
      s.pageFilter = (_i, p) => p.kind === 'page' && has(p.inFrame, ent.entityId);
      s.title = !!t.nameInTitle;
      s.covers = state.illustrations.filter((i) => t.nameInTitle || (coverOf(i) && has(coverOf(i)!.inFrame, ent.entityId))).map((i) => i.illustrationId);
      s.sheets = (i) => i.sheets.filter((r) => r.entityId === ent.entityId);
      s.translations = true;
      return s;
    }
    case 'add_block': {
      if (!t.blockId || block) return null; // the new block id must be new
      s.blocks = [t.blockId];
      s.narrations = allNarr;
      s.blockIlls = allIll;
      s.placeholderBlock = t.blockId;
      s.sheets = (i) => missingSheets(i, t.newStates);
      return s;
    }
    case 'delete_block': {
      if (!block) return null;
      s.blocks = [block.blockId];
      s.narrations = allNarr;
      s.blockIlls = allIll;
      return s;
    }
    case 'reorder': {
      const ids = t.blockIds ?? [];
      const cur = [...state.blocks].sort((a, b) => a.index - b.index).map((b) => b.blockId);
      if (ids.length !== cur.length || new Set(ids).size !== ids.length || ids.some((id) => !cur.includes(id))) return null;
      s.blocks = ids.filter((id, i) => cur[i] !== id); // moved blocks: scene + continuity check
      s.narrations = allNarr;
      return s;
    }
    case 'redraw_page':
    case 'balloon': {
      const page = ill?.pages.find((p) => p.pageId === t.pageId && p.kind === 'page');
      if (!ill || !page) return null;
      s.page = { illustrationId: ill.illustrationId, pageId: page.pageId };
      if (kind === 'redraw_page') s.sheets = (i) => (i.illustrationId === ill.illustrationId ? missingSheets(i, t.inFrame) : []);
      return s;
    }
    case 'cover_cast': {
      if (!ill || !coverOf(ill)) return null;
      s.covers = [ill.illustrationId];
      s.sheets = (i) => (i.illustrationId === ill.illustrationId ? missingSheets(i, t.inFrame) : []);
      return s;
    }
    case 'recording':
    case 'block_voice': {
      if (!narr || !block) return null;
      s.blocks = [block.blockId];
      s.narrations = [narr.narrationId];
      return s;
    }
    case 'music': {
      if (!narr) return null;
      s.narrations = [narr.narrationId];
      return s;
    }
    case 'dedication':
      return s;
    case 'delete_hero': {
      if (!ent) return null;
      s.entities = [ent.entityId];
      s.pageFilter = (_i, p) => p.kind === 'page' && has(p.inFrame, ent.entityId);
      s.covers = state.illustrations.filter((i) => coverOf(i) && has(coverOf(i)!.inFrame, ent.entityId)).map((i) => i.illustrationId);
      s.sheets = (i) => i.sheets.filter((r) => r.entityId === ent.entityId);
      return s;
    }
    case 'delete_story': {
      s.entities = state.entities.map((e) => e.entityId);
      s.blocks = state.blocks.map((b) => b.blockId);
      s.narrations = allNarr;
      s.blockIlls = allIll;
      s.covers = allIll;
      s.sheets = (i) => i.sheets;
      s.title = true;
      return s;
    }
    default:
      return null;
  }
}

/** Concrete refs of one artifact kind in the scope. */
function expand(artifact: string, klass: Klass, kind: EditKind, s: Scope, state: StoryEditState): ArtifactRef[] {
  const out: ArtifactRef[] = [];
  const ills = state.illustrations;
  const narrs = state.narrations.filter((n) => s.narrations.includes(n.narrationId));
  const pagesOf = (ill: EditIllustration): EditPage[] => {
    if (s.page) return ill.illustrationId === s.page.illustrationId ? ill.pages.filter((p) => p.pageId === s.page!.pageId) : [];
    return ill.pages.filter((p) => p.kind === 'page' && (
      (s.blockIlls.includes(ill.illustrationId) && p.blockId !== null && s.blocks.includes(p.blockId))
      || (s.pageFilter?.(ill, p) ?? false)));
  };
  switch (artifact) {
    case 'text': case 'scene': case 'scene_x': case 'check': case 'newent': case 'prep': case 'dir':
      for (const b of s.blocks) out.push({ artifact, key: `${artifact}:${b}`, blockId: b });
      break;
    case 'title':
      if (s.title) out.push({ artifact, key: 'title' });
      break;
    case 'ent':
      for (const e of s.entities) out.push({ artifact, key: `ent:${e}`, entityId: e, ...(kind === 'hero_version' ? { note: 'new_hero_version' as const } : {}) });
      break;
    case 'audio': case 'voice': case 'rec':
      for (const n of narrs) for (const b of s.blocks) {
        if (artifact === 'rec' && !n.recordings.includes(b)) continue;
        const rerecord = artifact === 'audio' && klass === 'stale' && n.recordings.includes(b) && kind !== 'block_voice';
        out.push({ artifact, key: `${artifact}:${n.narrationId}:${b}`, narrationId: n.narrationId, blockId: b, ...(rerecord ? { note: 'rerecord' as const } : {}) });
      }
      break;
    case 'music': case 'afile':
      for (const n of narrs) out.push({ artifact, key: `${artifact}:${n.narrationId}`, narrationId: n.narrationId });
      break;
    case 'sb':
      for (const ill of ills) if (s.blockIlls.includes(ill.illustrationId)) for (const b of s.blocks)
        out.push({ artifact, key: `sb:${ill.illustrationId}:${b}`, illustrationId: ill.illustrationId, blockId: b });
      if (s.page) out.push({ artifact, key: `sb:${s.page.illustrationId}:${s.page.pageId}`, illustrationId: s.page.illustrationId, pageId: s.page.pageId });
      break;
    case 'page': case 'refsp': case 'balloon':
      for (const ill of ills) {
        if (artifact === 'balloon' && ill.template !== 'comic') continue;
        if (s.placeholderBlock && klass === 'new') {
          if (s.blockIlls.includes(ill.illustrationId)) {
            out.push({ artifact, key: `${artifact}:${ill.illustrationId}:${s.placeholderBlock}:*`, illustrationId: ill.illustrationId, blockId: s.placeholderBlock });
          }
          continue;
        }
        for (const p of pagesOf(ill)) out.push({
          artifact, key: `${artifact}:${ill.illustrationId}:${p.pageId}`, illustrationId: ill.illustrationId, pageId: p.pageId,
          blockId: p.blockId ?? undefined, ...(artifact === 'balloon' && klass === 'stale' ? { note: 'marked' as const } : {}),
        });
      }
      break;
    case 'sbc': case 'refsc': case 'cover':
      for (const i of s.covers) out.push({ artifact, key: `${artifact}:${i}`, illustrationId: i });
      break;
    case 'sheet':
      for (const ill of ills) for (const r of s.sheets(ill))
        out.push({ artifact, key: `sheet:${ill.illustrationId}:${r.entityId}:${r.stateId}`, illustrationId: ill.illustrationId, entityId: r.entityId, stateId: r.stateId });
      break;
    case 'transl':
      if (s.translations) for (const tr of state.translations) for (const b of s.blocks)
        out.push({ artifact, key: `transl:${tr.storyId}:${b}`, storyId: tr.storyId, blockId: b, note: 'source_changed' });
      break;
    default:
      break; // order, hero, ded: roots only; exports: exportsOf()
  }
  return out;
}

/** Export rows whose inputs the edit touches. */
function exportsOf(kinds: string[], klass: Klass, rule: EditRule, touched: ArtifactRef[], state: StoryEditState): ArtifactRef[] {
  const want = new Set(kinds.filter((k) => EXPORT_ARTIFACTS.has(k)));
  if (!want.size) return [];
  const orderRoot = rule.root.includes('order');
  const dedRoot = rule.root.includes('ded');
  const ills = new Set(touched.filter((r) => ILL_ARTIFACTS.has(r.artifact) && r.illustrationId).map((r) => r.illustrationId!));
  const narrs = new Set(touched.filter((r) => (r.artifact === 'afile' || r.artifact === 'audio' || r.artifact === 'rec') && r.narrationId).map((r) => r.narrationId!));
  const text = orderRoot || touched.some((r) => r.artifact === 'text' || r.artifact === 'title');
  const illHit = (id?: string | null) => (id ? ills.has(id) : ills.size > 0);
  const narrHit = (id?: string | null) => (id ? narrs.has(id) : narrs.size > 0);
  const out: ArtifactRef[] = [];
  for (const x of state.exports) {
    if (!want.has(x.kind)) continue;
    if (NEVER.includes(x.kind) && klass !== 'gone') continue;
    let hit: boolean;
    switch (x.kind) {
      case 'pdf': case 'print': hit = dedRoot || illHit(x.illustrationId) || (orderRoot && (!!x.illustrationId || state.illustrations.length > 0)); break;
      case 'video': case 'pub': hit = text || illHit(x.illustrationId) || narrHit(x.narrationId); break;
      // YouTube never changes: only «delete the hero» lists it, taken down by hand
      case 'yt': hit = klass === 'gone'; break;
      default: hit = false;
    }
    if (!hit) continue;
    out.push({
      artifact: x.kind, key: `export:${x.exportId}`, exportId: x.exportId,
      illustrationId: x.illustrationId ?? undefined, narrationId: x.narrationId ?? undefined,
      ...(x.kind === 'pub' && klass === 'stale' ? { note: 'new_version' as const } : {}),
      ...(x.kind === 'yt' ? { note: 'manual' as const } : {}),
    });
  }
  return out;
}

function uniq(refs: ArtifactRef[]): ArtifactRef[] {
  const seen = new Set<string>();
  return refs.filter((r) => (seen.has(r.key) ? false : (seen.add(r.key), true)));
}

/** Paid units of «обновить» / «нарисовать / озвучить». */
export function estimateRefs(
  refs: ArtifactRef[], state: StoryEditState, rates?: PlanEditOptions['rates'], freeRedrawPage?: string,
): EditEstimate {
  const e: EditEstimate = { credits: 0, pages: 0, covers: 0, sheets: 0, audioBlocks: 0, assemblies: 0 };
  for (const r of refs) {
    switch (r.artifact) {
      case 'page': {
        if (freeRedrawPage && r.key === freeRedrawPage) break;
        const ill = state.illustrations.find((i) => i.illustrationId === r.illustrationId);
        e.pages += r.key.endsWith(':*') ? (ill?.template === 'comic' ? MAX_NEW_PAGES_PER_COMIC_BLOCK : 1) : 1;
        break;
      }
      case 'cover': e.covers += 1; break;
      case 'sheet': e.sheets += 1; break;
      case 'audio': e.audioBlocks += 1; break;
      case 'afile': e.assemblies += 1; break;
      default: break;
    }
  }
  if (rates) {
    e.credits = e.pages * rates.page + e.covers * rates.cover + e.sheets * rates.reference
      + e.audioBlocks * rates.blockAudio + e.assemblies * rates.assembly;
  }
  return e;
}

export function planEdit(kind: EditKind, target: EditTarget, state: StoryEditState, opts: PlanEditOptions = {}): EditPlan {
  const meta = EDIT_KINDS[kind];
  if (!meta) return forbidden(kind, 'unknown_target');
  const rule = EDITS[meta.rule];
  if (kind === 'format_style') return forbidden(kind, 'new_illustration');
  if (state.locks?.generating.length) return forbidden(kind, 'generating');

  // pre-checks on the target
  const block = target.blockId ? state.blocks.find((b) => b.blockId === target.blockId) : undefined;
  if ((kind === 'delete_block' || kind === 'block_text' || kind === 'block_regen') && block?.index === 0) return forbidden(kind, 'block0_pinned');
  if (kind === 'reorder') {
    const b0 = state.blocks.find((b) => b.index === 0);
    if (b0 && target.blockIds && target.blockIds[0] !== b0.blockId) return forbidden(kind, 'block0_pinned');
  }
  if (kind === 'block_voice') {
    const n = state.narrations.find((x) => x.narrationId === target.narrationId);
    if (n && (!target.voice || !n.voices.includes(target.voice))) return forbidden(kind, 'voice_model');
  }
  if (kind === 'block_regen' && (target.regenAttempts ?? 0) >= MAX_BLOCK_REGENS) return forbidden(kind, 'regen_limit');
  if (kind === 'balloon') {
    const ill = state.illustrations.find((i) => i.illustrationId === target.illustrationId);
    if (ill && ill.template !== 'comic') return forbidden(kind, 'not_comic');
  }

  const scope = scopeOf(kind, target, state);
  if (!scope) return forbidden(kind, 'unknown_target');

  const build = (klass: Klass) => uniq(rule[klass].flatMap((a) => expand(a, klass, kind, scope, state)));
  const autoSteps = build('auto');
  let stale = build('stale');
  const fresh = build('new');
  let gone = build('gone');
  // exports: by what the edit touches
  const touched = [...autoSteps, ...stale, ...fresh, ...gone];
  stale = uniq([...stale, ...exportsOf(rule.stale, 'stale', rule, touched, state)]);
  gone = uniq([...gone, ...exportsOf(rule.gone, 'gone', rule, touched, state)]);

  // revert by input hash: same inputs as a stored version → back for free
  const reusable = new Set(opts.reusableKeys ?? []);
  const reused = stale.filter((r) => reusable.has(r.key));
  stale = stale.filter((r) => !reusable.has(r.key));

  // confirmations
  const confirm: ConfirmReason[] = [];
  const freeRedraw = kind === 'redraw_page' && (target.redrawsUsed ?? 0) < FREE_REDRAWS && scope.page
    ? `page:${scope.page.illustrationId}:${scope.page.pageId}` : undefined;
  if (kind === 'redraw_page' && !freeRedraw) confirm.push('paid_redraw');
  const estimate = estimateRefs([...stale, ...fresh], state, opts.rates, freeRedraw);
  const units = estimate.pages + estimate.covers + estimate.sheets + estimate.audioBlocks;
  if ((BIG_KINDS.has(kind) && units > 0) || units >= (opts.bigEditUnits ?? DEFAULT_BIG_EDIT_UNITS)) confirm.push('big_edit');
  if (kind === 'delete_block' && gone.some((r) => r.artifact === 'rec')) confirm.push('recording');
  if (kind === 'delete_story') {
    confirm.push('delete_story');
    if (state.translations.length) confirm.push('translations');
  }
  if (kind === 'delete_hero') confirm.push('delete_content');

  const notices: EditNotice[] = [];
  if (stale.some((r) => r.note === 'new_version')) notices.push('pub_new_version');
  if (stale.some((r) => r.artifact === 'transl')) notices.push('translation_source_changed');
  if (rule.root.includes('order') && kind !== 'delete_story') notices.push('continuity_warning');
  if (kind === 'hero_version') notices.push('hero_version_available');

  const plan: EditPlan = {
    kind, ed: meta.ed, rule: meta.rule, autoSteps, stale, new: fresh, gone, reused,
    confirmRequired: confirm, notices,
    ...(units > 0 || estimate.assemblies > 0 ? { estimate } : {}),
    ...(rule.deferred ? { deferred: true } : {}),
  };

  // an open print order freezes its illustration (and the dedication)
  const printOpen = new Set(state.locks?.printOpenFor ?? []);
  if (printOpen.size) {
    const hitsPrint = [...autoSteps, ...stale, ...fresh, ...gone].some((r) =>
      (r.illustrationId && printOpen.has(r.illustrationId) && (ILL_ARTIFACTS.has(r.artifact) || r.artifact === 'pdf' || r.artifact === 'print'))
      || (r.artifact === 'print' && !r.illustrationId));
    if (hitsPrint || kind === 'delete_story') return { ...plan, forbiddenReason: 'print_open' };
  }
  // auto LLM steps are free, with a rate limit
  if (autoSteps.some((r) => LLM_STEPS.has(r.artifact)) && (opts.recentAutoRuns ?? 0) >= (opts.autoRunLimit ?? DEFAULT_AUTO_RUN_LIMIT)) {
    return { ...plan, forbiddenReason: 'rate_limit' };
  }
  return plan;
}

/** Keys only, for the UI badge and the export guard. */
export const planKeys = (plan: EditPlan) => ({
  auto: plan.autoSteps.map((r) => r.key),
  stale: plan.stale.map((r) => r.key),
  new: plan.new.map((r) => r.key),
  gone: plan.gone.map((r) => r.key),
});
